import { fetchRemoteLots } from './api-operations';
import { mapRemoteDocument, applyRemoteSnapshot } from './lotOperations';
import { preloadEstimatesAfterLogin } from './estimates';
import { updatePriceReviewSpinnersFromData } from './priceReview';
import {
  schedulePolling,
  scheduleBidRefresh,
  scheduleLogoRefresh,
  triggerImmediatePoll,
} from './polling';
import { scheduleSyncIndicatorReveal, clearSyncIndicatorDelay } from './syncManager';
import { MESSAGE_TYPES } from '../../constants';
import type { RealTimeSubscriber } from './realTime';

export function createRemoteFetcher(deps: {
  cfg: any;
  state: any;
  ensureAccessToken: () => Promise<string | null>;
  render: () => void;
  applyModeBanner: () => void;
  pendingViewModeRefreshRef: { value: boolean };
  hasLoadedInitialLotsRef: { value: boolean };
  DEBUG: boolean;
  realTimeSubscriber?: RealTimeSubscriber;
}) {
  const {
    cfg,
    state,
    ensureAccessToken,
    render,
    applyModeBanner,
    pendingViewModeRefreshRef,
    hasLoadedInitialLotsRef,
    DEBUG,
    realTimeSubscriber,
  } = deps;

  let hasRegisteredNewSubmissionListener = false;

  const registerNewSubmissionListener = () => {
    if (hasRegisteredNewSubmissionListener) return;
    hasRegisteredNewSubmissionListener = true;

    const handleImmediateRefresh = () => {
      void triggerImmediatePoll(cfg, mapRemoteDocument, render, 'submission');
    };

    try {
      window.addEventListener('lotlingo:newSubmission', handleImmediateRefresh as EventListener);
    } catch {
      /* noop */
    }

    try {
      if (typeof chrome !== 'undefined' && chrome.runtime?.onMessage?.addListener) {
        chrome.runtime.onMessage.addListener(message => {
          if (message?.type === MESSAGE_TYPES.NEW_SUBMISSION) {
            handleImmediateRefresh();
          }
        });
      }
    } catch {
      /* noop */
    }
  };

  return function startRemoteLotsFetch() {
    registerNewSubmissionListener();
    realTimeSubscriber?.attachVisibilityHandler();
    void realTimeSubscriber?.connect('login');
    fetchRemoteLots(
      cfg,
      state,
      ensureAccessToken,
      mapRemoteDocument,
      applyRemoteSnapshot,
      preloadEstimatesAfterLogin,
      updatePriceReviewSpinnersFromData,
      render,
      applyModeBanner,
      (c: any) => schedulePolling(c, mapRemoteDocument, render),
      () => scheduleBidRefresh(cfg, mapRemoteDocument, render),
      () => scheduleLogoRefresh(cfg, mapRemoteDocument, render),
      () => scheduleSyncIndicatorReveal(applyModeBanner, cfg),
      clearSyncIndicatorDelay,
      pendingViewModeRefreshRef,
      hasLoadedInitialLotsRef,
      DEBUG
    );
  };
}
